import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { PAGE_PARAM, parsePageParam, updateSearchParams } from "@/utils/search-params";

interface UsePaginationProps {
  pageSize: number;
  onChange?: (page: number) => void;
}

export default function usePagination({ pageSize, onChange }: UsePaginationProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const pageParam = useMemo(() => searchParams.get(PAGE_PARAM), [searchParams]);

  const changeCurrentPage = useCallback(
    (page: number) => {
      if (page === currentPage) return;
      onChange?.(page);
      setCurrentPage(page);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          updateSearchParams(next, { [PAGE_PARAM]: page.toString() });
          return next;
        },
        { preventScrollReset: true },
      );
    },
    [currentPage, onChange, setSearchParams],
  );

  const changeTotalPages = useCallback(
    (total: number) => {
      setTotalPages(getTotalPages(total, pageSize));
    },
    [pageSize],
  );

  useEffect(() => {
    setCurrentPage(parsePageParam(pageParam));
  }, [pageParam]);

  return { currentPage, changeCurrentPage, totalPages, changeTotalPages };
}

function getTotalPages(total: number, pageSize: number): number {
  return Math.ceil((total ?? 1) / pageSize);
}
